import React, { useState } from 'react'
import PropTypes from 'prop-types'

import { CircularProgress, Table, TableBody, TableCell, TableRow } from '@material-ui/core'
import { makeStyles, useTheme } from '@material-ui/core/styles'

import { DataTablePagination } from './DataTablePagination'
import SearchBox from './SearchBox'
import { TableHeader } from './Table'
import {
	ColumnFiltersState,
	flexRender,
	getCoreRowModel,
	getFacetedRowModel,
	getFacetedUniqueValues,
	getFilteredRowModel,
	getPaginationRowModel,
	getSortedRowModel,
	SortingState,
	useReactTable
} from '@tanstack/react-table'

const useStyles = makeStyles(theme => ({
	headerCell: {
		fontWeight: 'bold',
		textAlign: 'center',
		verticalAlign: 'middle',
		border: 'none'
	},
	bgtheme: {
		backgroundColor:
			theme.palette.mode === 'dark'
				? theme.palette.background.paper
				: theme.palette.grey[100]
	},
	evenRow: {
		backgroundColor:
			theme.palette.mode === 'dark'
				? theme.palette.background.paper
				: theme.palette.common.white
	},
	oddRow: {
		backgroundColor:
			theme.palette.mode === 'dark'
				? theme.palette.background.paper
				: theme.palette.grey[100]
	},
	cell: {
		padding: '2px',
		border: 'none'
	},
	loadingContainer: {
		display: 'flex',
		justifyContent: 'center',
		alignItems: 'center',
		padding: '2px'
	},
	noDataContainer: {
		display: 'flex',
		justifyContent: 'center',
		alignItems: 'center',
		height: '120px',
		color: '#777'
	}
}))

export default function CustomTable({
	loading,
	tableData,
	tableColumns,
	noDataMessage = 'No data available'
}) {
	const [rowSelection, setRowSelection] = useState({})
	const [columnFilters, setColumnFilters] = useState<ColumnFiltersState>([])
	const [sorting, setSorting] = useState<SortingState>([])
	const [globalFilter, setGlobalFilter] = useState('')

	const theme = useTheme() // Get the current theme (light or dark)
	const classes = useStyles()

	// Dynamically determine row background colors based on the theme
	const evenRowBackground =
		theme.palette.mode === 'dark' ? theme.palette.background.paper : theme.palette.grey[100]
	const oddRowBackground =
		theme.palette.mode === 'dark'
			? theme.palette.background.default
			: theme.palette.grey[200]
	const headerBackground = theme.palette.mode === 'dark' ? '#444' : '#8c7dff'

	const table = useReactTable({
		data: tableData || [],
		columns: tableColumns || [],
		state: {
			sorting,
			rowSelection,
			columnFilters,
			globalFilter
		},
		enableRowSelection: true,
		onRowSelectionChange: setRowSelection,
		onSortingChange: setSorting,
		onColumnFiltersChange: setColumnFilters,
		getCoreRowModel: getCoreRowModel(),
		getFilteredRowModel: getFilteredRowModel(),
		getPaginationRowModel: getPaginationRowModel(),
		getSortedRowModel: getSortedRowModel(),
		getFacetedRowModel: getFacetedRowModel(),
		getFacetedUniqueValues: getFacetedUniqueValues(),
		onGlobalFilterChange: setGlobalFilter,
		globalFilterFn: 'includesString'
	})

	return (
		<div style={{ display: 'flex', flexDirection: 'column', gap: '16px' }}>
			<div
				style={{
					display: 'flex',
					alignItems: 'center',
					justifyContent: 'space-between',
					gap: '16px'
				}}
			>
				<SearchBox
					placeholder="Search all columns..."
					inputValue={globalFilter ?? ''}
					handleSearch={e => setGlobalFilter(e.target.value)}
				/>
				<DataTablePagination table={table} />
			</div>

			<div style={{ borderRadius: '8px', border: 'none', overflow: 'hidden' }}>
				<Table>
					<TableHeader>
						{table.getHeaderGroups().map(headerGroup => (
							<TableRow key={headerGroup.id}>
								{headerGroup.headers.map(header => (
									<TableCell
										key={header.id}
										colSpan={header.colSpan}
										className={classes.headerCell}
										style={{
											fontWeight: 'bold',
											border: 'none',
											textAlign: 'center',
											verticalAlign: 'middle',
											color: 'white', // Always white
											backgroundColor: headerBackground // Dynamic background for header
										}}
									>
										{header.isPlaceholder
											? null
											: flexRender(header.column.columnDef.header, header.getContext())}
									</TableCell>
								))}
							</TableRow>
						))}
					</TableHeader>

					<TableBody>
						{table.getRowModel().rows?.length ? (
							table.getRowModel().rows.map((row, rowIndex) => (
								<TableRow
									key={row.id}
									data-state={row.getIsSelected() && 'selected'}
									className={rowIndex % 2 === 0 ? classes.evenRow : classes.oddRow}
									style={{
										backgroundColor:
											rowIndex % 2 === 0 ? evenRowBackground : oddRowBackground // Dynamic row background color
									}}
								>
									{row.getVisibleCells().map(cell => (
										<TableCell
											key={cell.id}
											className={classes.cell}
											style={{
												textAlign: 'center',
												verticalAlign: 'middle'
											}}
										>
											{flexRender(cell.column.columnDef.cell, cell.getContext())}
										</TableCell>
									))}
								</TableRow>
							))
						) : loading ? (
							<TableRow>
								<TableCell colSpan={tableColumns.length}>
									<div className={classes.loadingContainer}>
										<CircularProgress size={20} />
									</div>
								</TableCell>
							</TableRow>
						) : (
							<TableRow>
								<TableCell colSpan={tableColumns.length}>
									<div className={classes.noDataContainer}>{noDataMessage}</div>
								</TableCell>
							</TableRow>
						)}
					</TableBody>
				</Table>
			</div>
		</div>
	)
}

CustomTable.propTypes = {
	loading: PropTypes.bool,
	tableData: PropTypes.arrayOf(PropTypes.object),
	tableColumns: PropTypes.arrayOf(PropTypes.object)
}
